import { Gizmo } from '../../gizmos';
import { IIndex, IIndexColumn, IIndexOperation } from '../../types';
import { gnov } from '../../utils';
import CommonBinder from '../common-binder';

class IndexBinder extends CommonBinder {
  /**
   * Index data
   */
  private _index?: IIndex;

  constructor(gizmo: Gizmo) {
    super(gizmo);
  }

  public init = (): Promise<void> => {
    return new Promise<void>(async resolve => {
      await this.pageChange({});
      resolve();
    });
  };

  /**
   * Get the current index data
   *
   * @returns {IIndex} index data
   */
  public getIndex = (): IIndex | undefined => {
    return this._index;
  };

  /**
   * Get the cell value that will be rendered on the platform layer
   *
   * @param {IIndexColumn} column column's configuration
   * @param {any} row data row
   * @returns object with the info that's going to be rendered
   */
  public getCell = ({ column, row }: IGetCellParams) => {
    if (column.path) {
      const value = gnov(row, column.path);
      return { v: value };
    }
    if (
      column.type === 'operations' &&
      column.operations &&
      Array.isArray(column.operations)
    ) {
      return column.operations.map((operation: IIndexOperation) => {
        return { v: operation.label, op: operation };
      });
    } else if (column.type === 'status' && row.status && column.status) {
      const statusFid = row.status;
      const status = column.status.find(s => s.f_id === statusFid);
      return { v: status ? status.title : '', status };
    }
    if (column.gizmo) {
      const value = gnov(row, `answers.${column.gizmo.fid}.0.value`);

      if (value !== undefined) {
        return { v: value };
      }
    }

    return undefined;
  };

  /**
   * When user has clicked or pressed on a cell this function will be called by the platform layer
   *
   * @param {IIndexColumn} column column's configuration
   * @param {any} row data row
   */
  public cellInteraction = ({
    column,
    row,
    action,
    operation,
    params,
  }: ICellInteraction): Promise<any> => {
    return new Promise<any>(async resolve => {
      if (column.path) {
        await this._gizmo.dispatchGizmoOperation({
          operation: 'navigation.link',
          params: {
            path: `/r?fid=${row.id}`,
          },
        });
      } else if (action && params) {
        return resolve(
          await this._gizmo.dispatchGizmoOperation({
            operation: action,
            params,
          })
        );
      } else if (column.gizmo) {
        await this._gizmo.dispatchGizmoOperation({
          operation: 'navigation.link',
          params: {
            path: `/rg?rgfid=${row.formId}&fid=${row.fid}`,
          },
        });
      } else if (column.type === 'operations' && operation) {
        if (operation.type === 'form.index') {
          let path = `/r?fid=${row.id}`;
          if (operation.path) {
            path = `${operation.path}?fid=${row.id}`;
          }

          await this._gizmo.dispatchGizmoOperation({
            operation: 'navigation.link',
            params: {
              path,
            },
          });
        } else if (operation.type === 'form.stats') {
          let path = `/s?fid=${row.id}`;
          if (operation.path) {
            path = `${operation.path}?fid=${row.id}`;
          }

          await this._gizmo.dispatchGizmoOperation({
            operation: 'navigation.link',
            params: {
              path,
            },
          });
        } else if (operation.type === 'register.show') {
          await this._gizmo.dispatchGizmoOperation({
            operation: 'navigation.link',
            params: {
              path: `/rg?rgfid=${row.formId}&fid=${row.fid}&m=s`,
            },
          });
        } else if (operation.type === 'register.edit') {
          await this._gizmo.dispatchGizmoOperation({
            operation: 'navigation.link',
            params: {
              path: `/rg?rgfid=${row.formId}&fid=${row.fid}`,
            },
          });
        } else if (operation.type === 'register.pdf') {
          return resolve(
            await this._gizmo.dispatchGizmoOperation({
              operation: 'pdf.download',
              params: {
                rgfid: row.formId,
                id: row.fid,
              },
            })
          );
        }
      }

      resolve(undefined);
    });
  };

  /**
   * Get page of elements from the server
   *
   * @param {number} page number of page we're going to retrieve from the server
   * @param {number} pageSize number of elements we're going to bring frmo the server
   *
   * @returns
   */
  public pageChange = ({
    page,
    pageSize,
  }: IPageChangeParams): Promise<void> => {
    return new Promise<void>(async resolve => {
      const config = this._gizmo.getConfig();
      const indexOps = config.ops?.index;
      const dao = this._gizmo.getDao();
      const params = this._gizmo.getParams();

      // TODO: get info from params
      if (dao && indexOps && indexOps.rowType === 'forms') {
        const index = await dao.getIndex({
          type: 'rootGizmo',
          gizmoConfig: config,
        });

        this._index = index;
      } else if (
        dao &&
        indexOps &&
        indexOps.rowType === 'form.registers' &&
        params.fid
      ) {
        const index = await dao.getIndex({
          type: 'rootGizmoData',
          fid: params.fid,
          page,
          pageSize,
          gizmoConfig: config,
        });

        this._index = index;
      }

      resolve();
    });
  };

  /**
   * Function intended to be used by the 'extra buttons' on the index
   *
   * @param {string} operation operation that's going to be sent
   * @param {any} params params with path that will be dispatched
   *
   * @returns {Promise<void>}
   */
  public dispatchOperation = ({
    operation,
    params,
  }: IDispatchOperationParams): Promise<any> => {
    return new Promise<any>(async resolve => {
      resolve(
        await this._gizmo.dispatchGizmoOperation({
          operation,
          params,
        })
      );
    });
  };

  /**
   * Function that will be called by the search bar
   *
   * @param {string} text text to search
   *
   * @returns
   */
  public textSearch = ({ text }: ITextSearch): Promise<IIndex | undefined> => {
    return new Promise<IIndex | undefined>(async resolve => {
      return resolve(
        await this._gizmo.getDao()?.textSearch({
          text,
          gizmoConfig: this._gizmo.getConfig(),
          rootGizmoFid: this._gizmo.getParams().fid,
        })
      );
    });
  };

  /**
   * Function that will be called when searching on a column
   *
   * @param {string[]} selectedKeys keys selected by the user on the UI, that's how antd works
   * @param {IIndexColumn} column column's configuration
   *
   * @returns
   */
  public columnSearch = ({
    selectedKeys,
    column,
  }: IColumnSearch): Promise<IIndex | undefined> => {
    return new Promise<IIndex | undefined>(async resolve => {
      if (column.gizmo) {
        return resolve(
          await this._gizmo.getDao()?.columnSearch({
            searchColumns: [
              {
                values: selectedKeys,
                gizmoFid: column.gizmo.fid,
              },
            ],
            rootGizmoFid: this._gizmo.getParams().fid,
            gizmoConfig: this._gizmo.getConfig(),
          })
        );
      }

      resolve(undefined);
    });
  };
}

interface IPageChangeParams {
  page?: number;
  pageSize?: number;
}

interface IGetCellParams {
  column: IIndexColumn;
  row: any;
}

interface ICellInteraction {
  column: IIndexColumn;
  row: any;
  action?: 'pdf.download';
  operation?: IIndexOperation;
  params?: {
    rgfid: string;
    fid?: string;
    id?: string;
  };
}

interface IDispatchOperationParams {
  operation: 'navigation.link' | 'export.download';
  params: {
    path?: string;
    fid?: string;
  };
}

interface ITextSearch {
  text: string;
}

interface IColumnSearch {
  /**
   * Keys selected by the user on the UI, that's how antd works
   */
  selectedKeys: string[];
  /**
   * Column's configuration
   */
  column: IIndexColumn;
}

export default IndexBinder;
